import { asyncWrap, fetchAPI, safeFetchAPI } from '@/api';
import { timeUtils, utils } from '@/helpers';
import { Setter, SetterFull } from '@/legacy-types';
import { VoiceDropLanguage } from '@/types';
import { create } from 'zustand';

type ExtendedVoiceDropLanguage = VoiceDropLanguage & {
	countryCode: string;
	genderCode: string;
	languageCode: string;
	platform: string;
};

type VoiceOptionsState = {
	loaded: boolean;

	languageOptions: { label: string; value: string }[];
	accentOptions: { label: string; value: string }[];
	genderOptions: { label: string; value: string }[];

	macroWhitelist: string[];
	selectedLanguage: string;
	voiceOption: string;
	languageSettings: {
		languageCode: string;
		countryCode: string;
		genderCode: string;
	};
	platformMapping: Record<string, string>;
	languageData: Record<string, ExtendedVoiceDropLanguage>;
	gendersByLanguageAndCountry: Record<string, Map<string, { label: string; value: string }[]>>;
	countriesByLanguage: Record<string, { label: string; value: string }[]>;

	loadingPreview: boolean;
	currentPlaybackTime: number;
	previewAudio: HTMLAudioElement | null;
	previewData:
		| {
				inputTxt?: string;
				inputKey?: string;
				audioURL?: string;
				duration?: number;
				playing?: boolean;
		  }
		| undefined;
};

type VoiceOptionsActions = {
	loadLanguageOptions: () => Promise<void>;
	setLanguageSettings: (settings: SetterFull<VoiceOptionsState['languageSettings']>) => void;
	generatePreview: (textToSpeak: string) => Promise<HTMLAudioElement | null>;
	setPreviewData: (data: SetterFull<VoiceOptionsState['previewData']>) => void;
	sendTestCall: (text: string, phone: string, type: 'ttv' | 'voiceFile') => Promise<void>;
};

export type VoiceOptionsStore = VoiceOptionsActions & VoiceOptionsState;

const initialState: VoiceOptionsState = {
	loaded: false,
	voiceOption: '',
	languageOptions: [],
	accentOptions: [],
	genderOptions: [],
	macroWhitelist: [],
	selectedLanguage: '',
	languageSettings: {
		languageCode: 'en',
		countryCode: 'US',
		genderCode: 'F',
	},
	platformMapping: {},
	languageData: {},
	countriesByLanguage: {},
	gendersByLanguageAndCountry: {},

	loadingPreview: false,
	currentPlaybackTime: 0,
	previewAudio: null,
	previewData: undefined,
};

export const useVoiceOptionsStore = create<VoiceOptionsStore>((set, get) => ({
	...initialState,
	async loadLanguageOptions() {
		fetchAPI('/listAvailableVoiceMacros/:uid').then((macros) => set({ macroWhitelist: macros }));

		const [langData, error] = await safeFetchAPI<Record<string, VoiceDropLanguage>>('/listAvailableVoices/:uid');
		if (error) {
			utils?.toaster?.addToast?.({
				message: error || 'Error fetching language options',
				type: 'danger',
			});
		}

		const platformMapping = new Map<string, string>();
		const languageData = new Map<string, ExtendedVoiceDropLanguage>();
		const languageOptions = new Map<string, string>(); // languageCode -> languageName
		const countriesByLanguage = new Map<string, { label: string; value: string }[]>();
		const gendersByLanguageAndCountry = new Map<string, Map<string, { label: string; value: string }[]>>();

		for (const [langKey, language] of Object.entries(langData ?? {})) {
			const [languageCode, countryCode, platform, genderCode] = langKey.split('-');

			if (!languageOptions.has(languageCode)) {
				languageOptions.set(languageCode, language.lngn);
			}

			const countries = countriesByLanguage.get(languageCode) ?? [];
			if (!countries.find((c) => c.value === countryCode)) {
				countries.push({ label: language.cn, value: countryCode });
			}
			countriesByLanguage.set(languageCode, countries);

			const genders = gendersByLanguageAndCountry.get(languageCode) ?? new Map<string, { label: string; value: string }[]>();
			if (genders.has(countryCode)) {
				const gendersList = genders.get(countryCode) ?? [];
				if (!gendersList.find((g) => g.value === genderCode)) {
					gendersList.push({ label: language.g, value: genderCode });
				}
				genders.set(countryCode, gendersList);
			} else {
				genders.set(countryCode, [{ label: language.g, value: genderCode }]);
			}
			gendersByLanguageAndCountry.set(languageCode, genders);

			platformMapping.set([languageCode, countryCode, genderCode].join('-'), platform);

			languageData.set(langKey, { ...language, countryCode, genderCode, languageCode, platform });
		}

		const nextState: Partial<VoiceOptionsState> = {
			languageData: Object.fromEntries(languageData),
			platformMapping: Object.fromEntries(platformMapping),
			languageOptions: Object.entries(Object.fromEntries(languageOptions)).map(([value, label]) => ({ label, value })),
			gendersByLanguageAndCountry: Object.fromEntries(gendersByLanguageAndCountry),
			countriesByLanguage: Object.fromEntries(countriesByLanguage),
			languageSettings: get().languageSettings,
		};

		const voiceOption = buildVoiceOption(nextState);

		set({
			voiceOption,
			...getOptions(nextState),
			...nextState,
		});
	},
	setLanguageSettings(settings) {
		const currSettings = get().languageSettings;
		const newSettings: VoiceOptionsState['languageSettings'] = typeof settings === 'function' ? settings(currSettings) : settings;

		// When they change the language, ensure we select theh first availabile accent
		if (newSettings.languageCode !== currSettings.languageCode) {
			newSettings.countryCode = (get().countriesByLanguage[newSettings.languageCode] ?? [])[0].value;
		}

		// When they change the country, select the first availiable gender option
		if (newSettings.countryCode !== currSettings.countryCode) {
			newSettings.genderCode = (get().gendersByLanguageAndCountry[newSettings.languageCode]?.get(newSettings.countryCode) ?? [])[0].value;
		}

		const nextState = { ...get(), languageSettings: newSettings };

		set({
			...nextState,
			voiceOption: buildVoiceOption(nextState),
			...getOptions(nextState),
		});
	},

	async generatePreview(textToSpeach: string) {
		const { previewData, voiceOption, previewAudio, setPreviewData } = get();

		if (previewData?.playing) {
			previewAudio?.pause();
			set({ previewData: { ...previewData, playing: false } });
			return null;
		}

		const inputKey = voiceOption || '';

		if (!textToSpeach) {
			utils.toaster?.addToast?.({
				message: 'Please enter a message to preview',
				type: 'danger',
			});
			return null;
		}
		if (!voiceOption) {
			utils.toaster?.addToast?.({
				message: 'Please select a language option',
				type: 'danger',
			});
			return null;
		}

		// If the input data is the same as the previous preview, just play the audio again
		if (previewData?.inputTxt === textToSpeach && previewData?.inputKey === inputKey) {
			previewAudio?.play();
			set({ previewData: { ...previewData, playing: true } });
			set({ currentPlaybackTime: 0 });
			return null;
		}

		// Check cooldown for previewing audio
		const startCooldown = utils.local.get('voicePreviewCooldown');
		const cooldownDuration = timeUtils.units.SECOND_MS * 5;
		if (startCooldown) {
			const remainingTime = startCooldown + cooldownDuration - Date.now();
			if (remainingTime > 0) {
				utils.toaster?.addToast?.({
					message: `Please wait ${Math.ceil(remainingTime / 1000)} seconds before previewing again`,
					type: 'danger',
				});
				return null;
			}
		}

		set({ loadingPreview: true });
		const [audioURL, error] = await safeFetchAPI('/previewTextToSpeech/:uid', {
			method: 'POST',
			payload: { txt: textToSpeach || '', key: voiceOption },
		});
		if (error) {
			utils?.toaster?.addToast?.({
				message: error || 'Error fetching language options',
				type: 'danger',
			});
			set({ previewData: undefined, loadingPreview: false, currentPlaybackTime: 0 });
			return null;
		}

		const audio = new Audio(audioURL);
		audio.play();
		audio.loop = false;
		setPreviewData((p) => ({ ...p, textToSpeach, inputKey, audioURL }));
		audio.addEventListener('loadedmetadata', () => setPreviewData((p) => ({ ...p, duration: audio.duration, playing: true })));
		audio.addEventListener('timeupdate', () => setPreviewData((p) => ({ ...p, currentPlaybackTime: audio.currentTime })));
		audio.addEventListener('ended', () => {
			setPreviewData((p) => ({ ...p, duration: audio.duration, playing: false }));
			set({ currentPlaybackTime: 0 });
			audio.remove();
		});

		set({ previewAudio: audio });

		utils.local.set('voicePreviewCooldown', Date.now(), cooldownDuration);

		return audio;
	},

	setPreviewData(data: SetterFull<VoiceOptionsState['previewData']>) {
		const currData = get().previewData;
		const newData = typeof data === 'function' ? data(currData) : data;
		set({ previewData: newData });
	},

	async sendTestCall(value: string, phone: string, type: 'ttv' | 'voiceFile' = 'ttv') {
		const langOpt = get().voiceOption;
		if (!value) {
			return utils?.toaster?.addToast?.({
				type: 'danger',
				message: 'Enter a message to send',
			});
		}
		if (type === 'ttv' && !langOpt) {
			return utils?.toaster?.addToast?.({
				type: 'danger',
				message: 'Select a language option',
			});
		}
		if (!phone || !utils.phoneRex.test(phone)) {
			return utils?.toaster?.addToast?.({
				type: 'danger',
				message: 'Please enter a valid phone number',
			});
		}
		// Check cooldown for previewing audio
		const startCooldown = utils.local.get('voiceCallPreviewCooldown');
		const cooldownDuration = timeUtils.units.SECOND_MS * 10;
		if (startCooldown) {
			const remainingTime = startCooldown + cooldownDuration - Date.now();
			if (remainingTime > 0) {
				return utils?.toaster?.addToast?.({
					type: 'danger',
					message: `Please wait ${Math.ceil(remainingTime / 1000)} seconds before previewing again`,
				});
			}
		}
		utils.local.set('voiceCallPreviewCooldown', Date.now(), cooldownDuration);
		const [_, error] = await safeFetchAPI(`/sendTestCall/:uid/${phone}`, {
			method: 'POST',
			payload: { voiceTemplate: { [type]: value, lo: langOpt } },
		});
		if (error) {
			return utils?.toaster?.addToast?.({
				type: 'danger',
				message: error || 'Error sending test call',
			});
		}
		utils?.toaster?.addToast?.({
			type: 'success',
			message: 'Test call should be on its way!',
		});
	},
}));

const getOptions = (
	state: Partial<VoiceOptionsState>,
): {
	accentOptions: { label: string; value: string }[];
	genderOptions: { label: string; value: string }[];
} => {
	const accentOptions = state?.countriesByLanguage?.[state?.languageSettings?.languageCode ?? ''] ?? [];
	const genderOptions = state?.gendersByLanguageAndCountry?.[state?.languageSettings?.languageCode ?? '']?.get(state?.languageSettings?.countryCode ?? '') ?? [];

	return { accentOptions, genderOptions };
};

const buildVoiceOption = (state: Partial<VoiceOptionsState>): string => {
	const ls = state.languageSettings;
	const platformKey = [ls?.languageCode, ls?.countryCode, ls?.genderCode].join('-');
	const platform = state?.platformMapping?.[platformKey] || '';
	return [ls?.languageCode, ls?.countryCode, platform, ls?.genderCode].join('-');
};
